import API_BASE from '../../utils/api';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import UpcomingEventsScroller from '../../components/UpcomingEventsScroller';
import { useLicense } from '../../context/LicenseContext';
import { SkeletonBlock } from '../../components/Skeleton';

const ParentDashboard = () => {
  const { user } = useAuth();
  const { isModuleEnabled } = useLicense();
  const navigate = useNavigate();
  const [childData, setChildData] = useState(null);
  const [recentAttendance, setRecentAttendance] = useState([]);
  const [upcomingFees, setUpcomingFees] = useState([]);
  const [childLoading, setChildLoading] = useState(true);
  const [attendanceLoading, setAttendanceLoading] = useState(true);
  const [feesLoading, setFeesLoading] = useState(true);

  useEffect(() => {
    if (user?.studentId) {
      fetchChildData();
      fetchRecentAttendance();
      fetchUpcomingFees();
    } else {
      setChildLoading(false);
      setAttendanceLoading(false);
      setFeesLoading(false);
    }
  }, [user]);

  const fetchChildData = async () => {
    try {
      const response = await axios.get(`${API_BASE}/students/child/${user.studentId}`);
      setChildData(response.data);
    } catch (error) {
      console.error('Error fetching child data:', error);
    } finally {
      setChildLoading(false);
    }
  };

  const fetchRecentAttendance = async () => {
    try {
      const response = await axios.get(`${API_BASE}/attendance/student/${user.studentId}`);
      setRecentAttendance(response.data.slice(0, 5)); // Last 5 records
    } catch (error) {
      console.error('Error fetching attendance:', error);
    } finally {
      setAttendanceLoading(false);
    }
  };

  const fetchUpcomingFees = async () => {
    try {
      const response = await axios.get(`${API_BASE}/fees/student/${user.studentId}?status=pending`);
      setUpcomingFees(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching fees:', error);
    } finally {
      setFeesLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-800">Parent Dashboard</h1>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="p-6">
          <UpcomingEventsScroller />
          {/* Child Information Card */}
          {childLoading ? (
            <div className="mb-6"><SkeletonBlock rows={1} /></div>
          ) : childData && (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-lg font-semibold mb-4">My Child's Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Name</p>
                  <p className="font-medium">{childData.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Roll Number</p>
                  <p className="font-medium">{childData.rollNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Class</p>
                  <p className="font-medium">{childData.classId?.className} - {childData.classId?.section}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Overall Attendance</p>
                  <p className="font-medium text-green-600">92%</p>
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[
              { module: 'attendance', path: '/parent/attendance', icon: '📋', title: 'Attendance', desc: 'View attendance history' },
              { module: 'fees', path: '/parent/fees', icon: '💰', title: 'Fees', desc: 'Check fee status' },
              { module: 'transport', path: '/parent/transport', icon: '🚌', title: 'Transport', desc: 'Bus route details' },
              { module: null, path: '/parent/child', icon: '👶', title: 'Child Profile', desc: 'View details' },
            ].filter(item => isModuleEnabled(item.module)).map(item => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className="p-4 bg-white rounded-lg shadow hover:shadow-lg transition flex items-center space-x-3"
              >
                <span className="text-3xl">{item.icon}</span>
                <div className="text-left">
                  <h3 className="font-semibold">{item.title}</h3>
                  <p className="text-sm text-gray-500">{item.desc}</p>
                </div>
              </button>
            ))}
          </div>

          {/* Recent Attendance */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {attendanceLoading ? <SkeletonBlock rows={4} /> : (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold mb-4">Recent Attendance</h2>
                {recentAttendance.length > 0 ? (
                  <div className="space-y-3">
                    {recentAttendance.map((record) => (
                      <div key={record._id} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                        <span className="text-sm text-gray-600">
                          {new Date(record.date).toLocaleDateString()}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          record.status === 'present' ? 'bg-green-100 text-green-800' :
                          record.status === 'absent' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {record.status}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No attendance records found</p>
                )}
              </div>
            )}

            {/* Upcoming Fees */}
            {feesLoading ? <SkeletonBlock rows={3} /> : (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold mb-4">Upcoming Fees</h2>
                {upcomingFees.length > 0 ? (
                  <div className="space-y-3">
                    {upcomingFees.map((fee) => (
                      <div key={fee._id} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                        <div>
                          <p className="font-medium capitalize">{fee.feeType} Fee</p>
                          <p className="text-xs text-gray-500">Due: {new Date(fee.dueDate).toLocaleDateString()}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold">₹{fee.amount}</p>
                          <span className="text-xs text-yellow-600">Pending</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No pending fees</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentDashboard;